import { ref, computed } from 'vue'
import apiInstance from '@/utils/api'
import { parseApiError, logError } from '@/utils/errorHandler'

// ===== ORDERS INTERFACES =====
export interface OrderTransferred {
  code: string
  label: string
  error: string
}

export interface OrderActions {
  can_view_logs: boolean
  can_view_matrix_logs: boolean
  can_view_payments: boolean
  can_sync_matrix: boolean
}

export interface Card {
  type: string | null
  last4: string | null
}

export interface PaymentActions {
  can_view_notes: boolean
  can_capture: boolean
  can_reverse: boolean
  can_refund: boolean
}

export interface Payment {
  id: number
  type: string
  pt: string
  card: Card
  xid: string
  transaction_id: string
  payment_id: string
  refund_id: string | null
  gateway_status: string
  status: string
  amount: string
  notes: string
  created_at: string
  actions: PaymentActions
}

export interface OrderListItem {
  id: number
  order_no: number
  invoice_no: number | string
  invoice_link?: string
  can_download_invoice?: boolean
  gateway: string
  order_transferred: OrderTransferred
  order_mode: number
  table_no: string
  amount: string
  amount_without_currency: number
  payment_status: string
  payment_method: string
  created_at: string
  payments_count: number
  log_file_path: string
  actions: OrderActions
}

export interface OrderErrorSummaryItem {
  type: 'order' | 'payment'
  message: string
  count: number
}

export interface OrdersListResponse {
  error: boolean
  data: {
    current_page: number
    last_page: number
    total: number
    per_page: number
    orders: OrderListItem[]
    error_summary: OrderErrorSummaryItem[]
  }
}

export interface OrdersFilter {
  order_type?: number | string
  order_no?: string | number
  order_posted?: string
  order_option_id?: number | string
  start_date?: string
  end_date?: string
  transaction_id?: string
  payment_id?: string
}

/**
 * Create and return orders module
 */
export const useOrders = () => {
  // ===== STATE =====
  const orders = ref<OrderListItem[]>([])
  const errorSummary = ref<OrderErrorSummaryItem[]>([])
  const currentPage = ref(1)
  const lastPage = ref(1)
  const total = ref(0)
  const perPage = ref(50)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Sorting state
  const sortBy = ref<string>('created_at')
  const sortDir = ref<'asc' | 'desc'>('desc')

  // Filtering state
  const filters = ref<OrdersFilter>({
    order_type: '',
    order_no: '',
    order_posted: '',
    order_option_id: '',
    start_date: '',
    end_date: '',
    transaction_id: '',
    payment_id: '',
  })

  /**
   * Build query params from orders state
   */
  const buildQueryParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      per_page: perPage.value,
      page: currentPage.value,
      sort_by: sortBy.value,
      sort_dir: sortDir.value,
    }

    // Add non-empty filter values
    Object.entries(filters.value).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        params[key] = value
      }
    })

    return params
  }

  /**
   * Fetch orders list from API
   */
  const fetchOrders = async (restaurantId: string): Promise<void> => {
    if (!restaurantId) return

    isLoading.value = true
    error.value = null

    try {
      const params = buildQueryParams()

      const response = await apiInstance.get<OrdersListResponse>(
        `/restaurant/${restaurantId}/orders-list`,
        { params }
      )

      if (response.data.error === false) {
        const { current_page, last_page, total: totalOrders, per_page, orders: ordersList, error_summary } = response.data.data
        orders.value = ordersList
        errorSummary.value = error_summary ?? []
        currentPage.value = current_page
        lastPage.value = last_page
        total.value = totalOrders
        perPage.value = per_page
      } else {
        throw new Error('Failed to fetch orders')
      }
    } catch (err: any) {
      const apiError = parseApiError(err, 'Failed to fetch orders')
      error.value = apiError.message
      logError('Fetch orders', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Go to specific page
   */
  const goToPage = async (page: number, restaurantId: string): Promise<void> => {
    currentPage.value = page
    await fetchOrders(restaurantId)
  }

  /**
   * Change number of items per page
   */
  const changePerPage = async (newPerPage: number, restaurantId: string): Promise<void> => {
    perPage.value = newPerPage
    currentPage.value = 1
    await fetchOrders(restaurantId)
  }

  /**
   * Sort by column
   */
  const setSortBy = async (column: string, restaurantId: string): Promise<void> => {
    if (sortBy.value === column) {
      sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortBy.value = column
      sortDir.value = 'asc'
    }
    currentPage.value = 1
    await fetchOrders(restaurantId)
  }

  /**
   * Update orders filters and fetch data
   */
  const applyFilters = async (newFilters: Partial<OrdersFilter>, restaurantId: string): Promise<void> => {
    filters.value = { ...filters.value, ...newFilters }
    currentPage.value = 1
    await fetchOrders(restaurantId)
  }

  /**
   * Reset orders filters
   */
  const resetFilters = async (restaurantId: string): Promise<void> => {
    filters.value = {
      order_type: '',
      order_no: '',
      order_posted: '',
      order_option_id: '',
      start_date: '',
      end_date: '',
      transaction_id: '',
      payment_id: '',
    }
    currentPage.value = 1
    sortBy.value = 'created_at'
    sortDir.value = 'desc'
    await fetchOrders(restaurantId)
  }

  /**
   * Get order by ID
   */
  const getOrderById = (id: number): OrderListItem | undefined => {
    return orders.value.find((order) => order.id === id)
  }

  /**
   * Initialize orders state
   */
  const initialize = () => {
    orders.value = []
    errorSummary.value = []
    currentPage.value = 1
    lastPage.value = 1
    total.value = 0
    perPage.value = 50
    isLoading.value = false
    error.value = null
    sortBy.value = 'created_at'
    sortDir.value = 'desc'
    filters.value = {
      order_type: '',
      order_no: '',
      order_posted: '',
      order_option_id: '',
      start_date: '',
      end_date: '',
      transaction_id: '',
      payment_id: '',
    }
  }

  /**
   * Computed properties
   */
  const hasOrders = computed(() => orders.value.length > 0)
  const hasNextPage = computed(() => currentPage.value < lastPage.value)
  const hasPreviousPage = computed(() => currentPage.value > 1)
  const isFirstPage = computed(() => currentPage.value === 1)
  const isLastPage = computed(() => currentPage.value === lastPage.value)

  return {
    // State
    orders,
    errorSummary,
    currentPage,
    lastPage,
    total,
    perPage,
    isLoading,
    error,
    sortBy,
    sortDir,
    filters,

    // Methods
    fetchOrders,
    goToPage,
    changePerPage,
    setSortBy,
    applyFilters,
    resetFilters,
    getOrderById,
    initialize,

    // Computed
    hasOrders,
    hasNextPage,
    hasPreviousPage,
    isFirstPage,
    isLastPage,
  }
}
